import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import presetWebpage from "grapesjs-preset-webpage";
import newsletter from "grapesjs-preset-newsletter";
import "./Editor.styles.css";
import { AppContext } from "../../context/AppContext";
import Swal from "sweetalert2";
import axios from "axios";
import GetToken from "../../GetToken";
import { handleInvalidToken } from "../../AuthUtils";
import AnimationSpinner from "../../AnimationSpinner";
import { toast } from "react-toastify";
// react icons import
import { IoIosArrowRoundBack, IoIosArrowRoundForward } from "react-icons/io";
import { FaPaperPlane, FaPencilAlt, FaPlus, FaSave } from "react-icons/fa";
import { FaKeyboard } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

// Material UI import
import Button from "@mui/material/Button";

// custom component import
import TalkingBookStepper from "../../components/custom-components/BookFlow/TalkingBookStepper";

// utility functions
import { truncateTextByChars } from "../../util";

const PAGE_HEIGHT_LIMIT = 550;

const Editor = ({ bookToken, steps, setActiveStep, activeStep }) => {
	const editorRef = useRef(null);
	const [book, setBook] = useState([]);
	const [bookChapters, setBookChapters] = useState([]);
	const [pages, setPages] = useState([]);
	const [selectedChapter, setSelectedChapter] = useState(null);
	const [selectedPage, setSelectedPage] = useState(null);
	const { handleResize, base_url } = useContext(AppContext);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);

	const PAGE_HEIGHT_LIMIT = 560;

	const checkContentHeight = (editor) => {
		const canvasFrame = editor.Canvas.getFrameEl();
		if (!canvasFrame) return false;

		const canvasDocument =
			canvasFrame.contentDocument || canvasFrame.contentWindow.document;
		// const pageContentEl = canvasDocument.querySelector(".page-content");
		const contentHeightss = canvasDocument.documentElement.scrollHeight;

		if (contentHeightss > PAGE_HEIGHT_LIMIT) {
			// toast.error("Page height limit exceeded. Please reduce the content.");
			// Swal.fire({
			//     icon: "warning",
			//     title: "Page Limit Reached",
			//     text: "You cannot add more content as the page height limit has been exceeded.",
			// });

			return false;
		}

		return true;
	};

	useEffect(() => {
		fetchBookChapterData();
	}, []);

	const fetchBookChapterData = async () => {
		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);
			const formData = new FormData();
			formData.append("action", "getChapters");
			formData.append("token", token);
			formData.append("book_token", bookToken);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				const chapters = response.data.bookContentDetail || [];
				setBook(response.data.bookDetail);
				setBookChapters(chapters);
				if (chapters.length > 0) {
					setSelectedChapter(chapters[0]);
					fetchBookChapterPageData(chapters[0].ids);
				}
			} else {
				if (response.data.reset) {
					handleInvalidToken(navigate);
					return;
				}
				toast.error(response.data.message);
				// Swal.fire({ icon: "warning", title: "Warning", text: response.data.message });
			}
		} catch (error) {
			toast.error(
				error.response?.data?.message ||
					"Something went wrong. Please try again."
			);
			// Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Something went wrong. Please try again." });
		} finally {
			setTimeout(() => {
				setLoading(false);
			}, 1500);
		}
	};

	const fetchBookChapterPageData = async (chapterIds) => {
		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);
			const formData = new FormData();
			formData.append("action", "getChaptersPages");
			formData.append("token", token);
			formData.append("book_token", bookToken);
			formData.append("book_chapter_token", chapterIds);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				const pages = response.data.bookContentPages || [];
				setPages(pages);

				if (pages.length > 0) {
					setSelectedPage(pages[0].ids);
					editorRef.current.setComponents(`
                        <div class="page-content">${pages[0].content}</div>
                    `);
				}
			} else {
				if (response.data.reset) {
					handleInvalidToken(navigate);
					return;
				}
				toast.error(response.data.message);
				// Swal.fire({ icon: "warning", title: "Warning", text: response.data.message });
			}
		} catch (error) {
			toast.error(
				error.response?.data?.message ||
					"Something went wrong. Please try again."
			);
			// Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Something went wrong. Please try again." });
		} finally {
			setTimeout(() => {
				setLoading(false);
			}, 1500);
		}
	};

	useEffect(() => {
		handleResize();
	}, []);

	useEffect(() => {
		if (!editorRef.current) {
			const editor = grapesjs.init({
				container: "#chapter_editor",
				fromElement: true,
				width: "auto",
				height: "600px",
				storageManager: false,
				plugins: [newsletter],
				// pluginsOpts: { presetWebpage: {} },
			});

			// const bm = editor.BlockManager;
			editor.Panels.removeButton("options", "export-template");
			editor.Panels.removeButton("options", "gjs-open-import-template");
			editor.Panels.removeButton("options", "gjs-toggle-images");
			editor.Panels.removePanel("devices-c");

			editorRef.current = editor;
			// const bm = editor.BlockManager;

			// const originalAdd = bm.add;
			// bm.add = (id, block) => {
			//     if (!checkContentHeight(editor)) {
			//         Swal.fire({
			//             icon: "warning",
			//             title: "Page Limit Reached",
			//             text: "Cannot add more elements. The page height limit has been exceeded.",
			//         });
			//         return;
			//     }
			//     return originalAdd.call(bm, id, block);
			// };

			editor.on("component:add", (component) => {
				if (!checkContentHeight(editor)) {
					component.remove();
					showWarning(
						"Content exceeds the page limit! Consider adding a new page."
					);
				}
			});

			editor.on("asset:add", (asset) => {
				if (!checkContentHeight(editor)) {
					editor.AssetManager.remove(asset);
					showWarning(
						"Content exceeds the page limit! Consider adding a new page."
					);
				}
			});

			editor.on("component:update", (component) => {
				if (!checkContentHeight(editor)) {
					component.remove();
					showWarning(
						"Content exceeds the page limit! Consider adding a new page."
					);
				}
			});

			editor.on("canvas:dragEnd", (component) => {
				console.log("canvas:dragEnd", component);

				if (!checkContentHeight(editor)) {
					component.remove();
					showWarning(
						"Content exceeds the page limit! Consider adding a new page."
					);
				}
			});

			editor.on("component:styleUpdate", (component) => {
				if (!checkContentHeight(editor)) {
					component.setStyle({});
					showWarning(
						"Content exceeds the page limit! Consider adding a new page."
					);
				}
			});
		}
	}, []);

	const showWarning = (message) => {
		toast.warning(message);
		// Swal.fire({ icon: "warning", title: "Warning", text: message });
	};

	const handleChapterSelect = (chapter) => {
		setSelectedChapter(chapter);
		fetchBookChapterPageData(chapter.ids);
	};

	const handlePageSelect = (pageIndex) => {
		if (pages[pageIndex]) {
			setSelectedPage(pages[pageIndex].ids);
			editorRef.current.setComponents(`
                <div class="page-content">${pages[pageIndex].content}</div>
            `);
		}
	};

	const handleEditChapterTitle = async (chapter) => {
		const { value: newTitle } = await Swal.fire({
			title: "Edit Chapter Title",
			input: "text",
			inputValue: chapter.chapter_title,
			showCancelButton: true,
			confirmButtonText: "Update",
			preConfirm: (value) => {
				if (!value.trim()) {
					Swal.showValidationMessage("Title cannot be empty");
				}
				return value.trim();
			},
		});

		if (newTitle && newTitle !== chapter.chapter_title) {
			updateChapterTitle(chapter.ids, newTitle);
		}
	};

	const updateChapterTitle = async (chapterIds, newTitle) => {
		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);

			const formData = new FormData();
			formData.append("action", "updateChapterTitle");
			formData.append("token", token);
			formData.append("book_token", bookToken);
			formData.append("book_chapter_token", chapterIds);
			formData.append("chapter_title", newTitle);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				toast.success("Chapter title updated successfully!");
				// Swal.fire({ icon: "success", title: "Success", text: "Chapter title updated successfully!" });
				fetchBookChapterData();
			} else {
				toast.error(response.data.message);
				// Swal.fire({ icon: "error", title: "Error", text: response.data.message });
			}
		} catch (error) {
			toast.error(
				error.response?.data?.message ||
					"Something went wrong. Please try again."
			);
			// Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Something went wrong. Please try again." });
		} finally {
			setTimeout(() => {
				setLoading(false);
			}, 1000);
		}
	};

	const handleAddNewPage = async () => {
		if (!selectedChapter) {
			toast.warning("Please select a chapter first.");
			// Swal.fire({ icon: "warning", title: "Warning", text: "Please select a chapter first." });
			return;
		}

		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);

			const formData = new FormData();
			formData.append("action", "addChapterPage");
			formData.append("token", token);
			formData.append("book_token", bookToken);
			formData.append("book_chapter_token", selectedChapter.ids);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				toast.success("Page added successfully!");
				// Swal.fire({ icon: "success", title: "Success", text: "Page added successfully!" });
				fetchBookChapterPageData(selectedChapter.ids);
			} else {
				toast.error(response.data.message);
				// Swal.fire({ icon: "error", title: "Error", text: response.data.message });
			}
		} catch (error) {
			toast.error(
				error.response?.data?.message ||
					"Something went wrong. Please try again."
			);
			// Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Something went wrong. Please try again." });
		} finally {
			setLoading(false);
		}
	};

	const handleSavePageContent = async () => {
		if (!selectedChapter || !selectedPage) {
			toast.warning("Please select a chapter and a page to save.");
			// Swal.fire({ icon: "warning", title: "Warning", text: "No chapter or page selected to save." });
			return;
		}
		const editorContent = editorRef.current.getHtml();

		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);

			const formData = new FormData();
			formData.append("action", "updateChapterPageContent");
			formData.append("token", token);
			formData.append("book_token", bookToken);
			formData.append("book_chapter_token", selectedChapter.ids);
			formData.append("book_chapter_page_token", pages[0].ids);
			formData.append("content", editorContent);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				toast.success("Page content saved successfully!");
				// Swal.fire({ icon: "success", title: "Success", text: "Page content saved successfully!" });
			} else {
				toast.error(response.data.message);
				// Swal.fire({ icon: "error", title: "Error", text: response.data.message });
			}
		} catch (error) {
			toast.error(
				error.response?.data?.message ||
					"Something went wrong. Please try again."
			);
			// Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Something went wrong. Please try again." });
		} finally {
			setTimeout(() => {
				setLoading(false);
			}, 1000);
		}
	};
	const handleRemovePage = async (pageId) => {
		if (!selectedChapter || !pageId) {
			toast.warning("Please select a chapter and a page to remove.");
			// Swal.fire({ icon: "warning", title: "Warning", text: "No chapter or page selected to remove." });
			return;
		}

		const confirmDelete = await Swal.fire({
			title: "Are you sure?",
			text: "This action cannot be undone!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes, delete it!",
			cancelButtonText: "Cancel",
		});

		if (!confirmDelete.isConfirmed) return;

		try {
			const token = GetToken();
			if (!token) {
				handleInvalidToken(navigate);
				return;
			}
			setLoading(true);

			const formData = new FormData();
			formData.append("action", "removeChapterPage");
			formData.append("token", token);
			formData.append("book_token", bookToken);
			formData.append("book_chapter_token", selectedChapter.ids);
			formData.append("book_chapter_page_token", pageId);

			const response = await axios.post(`${base_url}api/user/book`, formData);

			if (response.data.status) {
				toast.success("Page removed successfully!");
				// Swal.fire({ icon: "success", title: "Success", text: "Page removed successfully!" });
				fetchBookChapterPageData(selectedChapter.ids);
			} else {
				toast.error(response.data.message);
				// Swal.fire({ icon: "error", title: "Error", text: response.data.message });
			}
		} catch (error) {
			toast.error(
				error.response?.data?.message ||
					"Something went wrong. Please try again."
			);
			// Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Something went wrong. Please try again." });
		} finally {
			setTimeout(() => {
				setLoading(false);
			}, 1000);
		}
	};

	const handleNext = () => {
		if (activeStep < steps.length - 1) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		}
	};
	const handleBack = () => {
		if (activeStep > 0) {
			setActiveStep((prevActiveStep) => prevActiveStep - 1);
		}
	};

	return (
		<>
			<AnimationSpinner show={loading} />
			<div className="row align-items-center">
				<div className="col-lg-6 page-title-container">
					<h4 className="fw-semibold mb-1">
						{book.title ? truncateTextByChars(book.title, 45) : "Loading..."}
					</h4>
					<p className="mb-1">Customize your book chapters </p>
				</div>
				<div className="col-lg-6 justify-content-end">
					<div className="d-flex justify-content-end gap-4">
						<Button
							onClick={handleBack}
							variant="contained"
							color="secondary"
							title="Go Back"
							className="ms-2"
							sx={{
								minWidth: "35px",
								height: "38px",
								padding: "0 17px",
							}}
						>
							<IoIosArrowRoundBack size={30} fontWeight={600} color="white" />
							Back
						</Button>
						<Button
							onClick={handleNext}
							variant="contained"
							color="secondary"
							title="Next step"
							className="ms-2"
							sx={{
								minWidth: "35px",
								height: "38px",
								padding: "0 17px",
							}}
						>
							Go to Next Step{" "}
							<span>
								<IoIosArrowRoundForward
									size={30}
									fontWeight={600}
									color="white"
								/>
							</span>
						</Button>
					</div>
				</div>
			</div>
			<div className="my-4">
				<TalkingBookStepper steps={steps} activeStep={activeStep} />
			</div>
			<div className="editor-container shadow">
				<div className="editor-sidebar text-center">
					<h4 className="fw-semibold text-start text-dark mb-3">Chapters</h4>
					{bookChapters.map((chapter, index) => (
						<div key={index} className="chapter-item">
							<button
								className={selectedChapter?.ids === chapter.ids ? "active" : "default"}
								onClick={() => handleChapterSelect(chapter)}
							>
								<p className="mb-0 text-start">{chapter.chapter_title}</p>
							</button>
							<button
								className="edit-btn"
								onClick={() => handleEditChapterTitle(chapter)}
							>
								<FaPencilAlt />
							</button>
						</div>
					))}
				</div>

				<div className="editor">
					<div id="chapter_editor" />
				</div>

				<div className="pages-panel">
					<button className="save-btn " onClick={handleSavePageContent}>
						<FaSave
							width={15}
							fontWeight={600}
							color="white"
							style={{ marginRight: "10px" }}
						/>
						Save Chapter
					</button>
					<div className="title-cover">
						<h3>Pages</h3>
						<button
							className="page-add-btn shadow-sm"
							onClick={handleAddNewPage}
						>
							<FaPlus />
						</button>
					</div>
					{pages.map((page, index) => {
						return (
                            <div key={index} className={`page-item ${selectedPage == page.ids ? "active" : ""}`} >
                            <button className="page" onClick={() => handlePageSelect(index)}>Page {index + 1}</button>
                            <button className="remove-page-btn shadow-sm" onClick={() => handleRemovePage(page.ids)}><MdDelete /></button>
                        </div>
						);
					})}
				</div>
			</div>
		</>
	);
};

export default Editor;
